import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ToggleSwitch as Switch } from "../ui/toggle-switch";
import { labelUdm, udmKind, udmStorageFactor } from "../../utils/udmUtils";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "../ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Package, Droplets, Flame, ChefHat, Search, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";

export const AggiungiComponenteDialog = ({
  open, onOpenChange,
  tipoComponente, onTipoChange,
  nuovoIngrediente, onNuovoIngredienteChange,
  ingredientiDisponibili, categorieIngredienti, ricette, editItem,
  scartiCache, onScartiCacheUpdate,
  getAuthHeaders, onAdd
}) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const axios = require("axios").default;
  const [search, setSearch] = useState("");
  const [selectedIngrediente, setSelectedIngrediente] = useState(null);

  // Mappa categorie
  const categorieMap = useMemo(() => {
    const map = {};
    (categorieIngredienti || []).forEach(c => { map[c.id] = c.nome; });
    return map;
  }, [categorieIngredienti]);

  // Filtra ingredienti
  const filteredIngredienti = useMemo(() => {
    if (!search.trim()) return ingredientiDisponibili;
    const s = search.toLowerCase();
    return ingredientiDisponibili.filter(i => i.nome?.toLowerCase().includes(s));
  }, [ingredientiDisponibili, search]);

  // Raggruppa
  const grouped = useMemo(() => {
    const groups = {};
    filteredIngredienti.forEach(i => {
      const cat = categorieMap[i.categoria_id] || "Altro";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(i);
    });
    const keys = Object.keys(groups).sort((a, b) => a === "Altro" ? 1 : b === "Altro" ? -1 : a.localeCompare(b, 'it'));
    keys.forEach(k => groups[k].sort((a, b) => a.nome.localeCompare(b.nome, 'it')));
    return { groups, keys };
  }, [filteredIngredienti, categorieMap]);

  const handleClose = (val) => {
    if (!val) {
      setSearch("");
      setSelectedIngrediente(null);
      onTipoChange("ingrediente");
      onNuovoIngredienteChange({ ingrediente_id: "", ricetta_id: "", quantita: "", peso_lordo: "", percentuale_scarto: 0, cotto: false, calo_cottura_percentuale: 0, is_acqua: false });
    }
    onOpenChange(val);
  };

  const handleSelectIngrediente = (ing) => {
    setSelectedIngrediente(ing);
    onNuovoIngredienteChange(prev => ({
      ...prev, ingrediente_id: ing.id, cotto: ing.cotto_default || false, calo_cottura_percentuale: ing.calo_cottura_percentuale || 0
    }));
    // Fetch scarto
    axios.get(`${API}/scarti/ingrediente/${ing.id}`, { headers: getAuthHeaders() })
      .then(res => {
        const perc = res.data?.percentuale_scarto || 0;
        onScartiCacheUpdate(prev => ({ ...prev, [ing.id]: perc }));
        onNuovoIngredienteChange(prev => ({ ...prev, percentuale_scarto: perc }));
      })
      .catch(() => { });
  };

  const handleAddAndClose = () => {
    onAdd();
    setSearch("");
    setSelectedIngrediente(null);
  };

  // Step: seleziona ingrediente o inserisci peso
  const showPesoForm = tipoComponente === "ingrediente" && (nuovoIngrediente.is_acqua || selectedIngrediente);
  const showRicettaForm = tipoComponente === "ricetta";

  return (
    <Drawer open={open} onOpenChange={handleClose}>
      <DrawerContent className="max-h-[85vh]" data-testid="aggiungi-ingrediente-drawer">
        <DrawerHeader className="text-left pb-2">
          <DrawerTitle>Aggiungi Componente</DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-2 overflow-y-auto flex-1">
          {/* Tabs tipo */}
          <Tabs value={tipoComponente} onValueChange={(v) => {
            onTipoChange(v);
            setSelectedIngrediente(null);
            setSearch("");
            onNuovoIngredienteChange({ ingrediente_id: "", ricetta_id: "", quantita: "", peso_lordo: "", percentuale_scarto: 0, cotto: false, calo_cottura_percentuale: 0, is_acqua: false });
          }}>
            <TabsList className="grid grid-cols-2 w-full h-11">
              <TabsTrigger value="ingrediente" className="gap-1.5 text-sm" data-testid="tab-ingrediente"><Package className="h-3.5 w-3.5" />Ingrediente</TabsTrigger>
              <TabsTrigger value="ricetta" className="gap-1.5 text-sm" data-testid="tab-ricetta"><ChefHat className="h-3.5 w-3.5" />Sotto-Ricetta</TabsTrigger>
            </TabsList>

            <TabsContent value="ingrediente" className="mt-4 space-y-4">
              {/* Toggle Acqua */}
              <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
                <div className="flex items-center gap-2"><Droplets className="h-4 w-4 text-blue-400" /><span className="font-medium text-sm">Acqua</span></div>
                <Switch checked={nuovoIngrediente.is_acqua} onCheckedChange={(v) => {
                  setSelectedIngrediente(null);
                  onNuovoIngredienteChange(prev => ({ ...prev, is_acqua: v, ingrediente_id: "" }));
                }} data-testid="acqua-switch" />
              </div>

              {!nuovoIngrediente.is_acqua && !selectedIngrediente && (
                <>
                  {/* Barra ricerca */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Cerca ingrediente..." value={search} onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 h-12 text-base" autoFocus data-testid="ingrediente-search" />
                  </div>
                  {/* Lista ingredienti raggruppati */}
                  <div className="space-y-1 max-h-[40vh] overflow-y-auto -mx-1 px-1">
                    {grouped.keys.map(gruppo => (
                      <div key={gruppo}>
                        <div className="sticky top-0 bg-background/90 backdrop-blur-sm px-2 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                          <Package className="h-3 w-3" />{gruppo} ({grouped.groups[gruppo].length})
                        </div>
                        {grouped.groups[gruppo].map(ing => (
                          <button key={ing.id} onClick={() => handleSelectIngrediente(ing)}
                            className={cn("w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors", "hover:bg-muted/50 active:scale-[0.98]")}
                            data-testid={`select-ing-${ing.id}`}>
                            <span className="font-medium text-sm">{ing.nome}</span>
                            <span className="text-xs text-muted-foreground">{(ing.prezzo_attuale || 0).toFixed(2)}/{ing.unita_misura || 'kg'}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                    {grouped.keys.length === 0 && (
                      <p className="text-center text-sm text-muted-foreground py-6">Nessun ingrediente trovato</p>
                    )}
                  </div>
                </>
              )}

              {/* Form peso - visibile dopo selezione ingrediente o acqua */}
              {showPesoForm && (
                <div className="space-y-4">
                  {selectedIngrediente && (
                    <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-xl border border-primary/20">
                      <Package className="h-5 w-5 text-lime-400" />
                      <div className="flex-1">
                        <p className="font-bold text-sm">{selectedIngrediente.nome}</p>
                        <p className="text-xs text-muted-foreground">{(selectedIngrediente.prezzo_attuale || 0).toFixed(2)}/{selectedIngrediente.unita_misura || 'kg'}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedIngrediente(null); onNuovoIngredienteChange(prev => ({ ...prev, ingrediente_id: "" })); }}
                        className="text-xs" data-testid="change-ingrediente-btn">Cambia</Button>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                      {!nuovoIngrediente.is_acqua && udmKind(selectedIngrediente?.unita_misura) === "pezzi" ? "Quantità" : "Peso"} ({nuovoIngrediente.is_acqua ? "ml" : labelUdm(selectedIngrediente?.unita_misura || "g")})
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground">Lordo</span>
                        <Input type="number" inputMode="decimal" value={nuovoIngrediente.peso_lordo}
                          onChange={(e) => {
                            const lordo = e.target.value;
                            const perc = nuovoIngrediente.percentuale_scarto || scartiCache[nuovoIngrediente.ingrediente_id] || 0;
                            const netto = lordo && perc > 0 ? String(+(parseFloat(lordo) * (1 - perc / 100)).toFixed(4)) : lordo;
                            onNuovoIngredienteChange(prev => ({ ...prev, peso_lordo: lordo, quantita: netto ? String(netto) : "" }));
                          }}
                          placeholder="1000" className="h-12 text-lg" data-testid="peso-lordo-input" />
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs text-muted-foreground">
                          Netto {nuovoIngrediente.percentuale_scarto > 0 && <span className="text-amber-400">(-{nuovoIngrediente.percentuale_scarto}%)</span>}
                        </span>
                        <Input type="number" inputMode="decimal" value={nuovoIngrediente.quantita}
                          onChange={(e) => {
                            const netto = e.target.value;
                            const lordo = nuovoIngrediente.peso_lordo;
                            let perc = 0;
                            if (lordo && netto && parseFloat(lordo) > parseFloat(netto)) perc = Math.round((parseFloat(lordo) - parseFloat(netto)) / parseFloat(lordo) * 1000) / 10;
                            onNuovoIngredienteChange(prev => ({ ...prev, quantita: netto, percentuale_scarto: perc }));
                          }}
                          placeholder="850" className="h-12 text-lg" data-testid="peso-netto-input" />
                      </div>
                    </div>
                  </div>

                  {/* Cottura */}
                  {!nuovoIngrediente.is_acqua && (
                    <div className="p-3 rounded-xl border border-border bg-muted/20 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><Flame className="h-4 w-4 text-orange-400" /><span className="font-medium text-sm">Cottura</span></div>
                        <Switch checked={nuovoIngrediente.cotto} onCheckedChange={(v) => onNuovoIngredienteChange(prev => ({ ...prev, cotto: v }))} data-testid="cotto-switch" />
                      </div>
                      {nuovoIngrediente.cotto && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Calo cottura (%)</Label>
                          <Input type="number" min="0" max="100" value={nuovoIngrediente.calo_cottura_percentuale}
                            onChange={(e) => onNuovoIngredienteChange(prev => ({ ...prev, calo_cottura_percentuale: e.target.value }))}
                            placeholder="20" className="h-12" data-testid="calo-cottura-input" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="ricetta" className="mt-4 space-y-4">
              <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/20">
                <p className="text-sm text-muted-foreground">Aggiungi un semilavorato. Il costo viene calcolato automaticamente.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Ricetta *</Label>
                <Select value={nuovoIngrediente.ricetta_id} onValueChange={(v) => onNuovoIngredienteChange(prev => ({ ...prev, ricetta_id: v }))}>
                  <SelectTrigger className="h-12"><SelectValue placeholder="Seleziona ricetta..." /></SelectTrigger>
                  <SelectContent>
                    {ricette.filter(r => !editItem || r.id !== editItem.id).map(r => (<SelectItem key={r.id} value={r.id}>{r.nome} ({(r.costo_per_kg || 0).toFixed(2)}/kg)</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Quantita (g) *</Label>
                <Input type="number" inputMode="decimal" value={nuovoIngrediente.quantita}
                  onChange={(e) => onNuovoIngredienteChange(prev => ({ ...prev, quantita: e.target.value }))}
                  placeholder="200" className="h-12 text-lg" data-testid="ricetta-qty-input" />
              </div>
              {nuovoIngrediente.ricetta_id && nuovoIngrediente.quantita && (
                <div className="p-3 bg-lime-500/10 rounded-xl border border-lime-500/20 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Costo stimato</span>
                  <span className="font-bold text-lime-400">
                    {(((parseFloat(nuovoIngrediente.quantita) || 0) / 1000) * (ricette.find(r => r.id === nuovoIngrediente.ricetta_id)?.costo_per_kg || 0)).toFixed(2)}
                  </span>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DrawerFooter className="flex-row gap-3 border-t pt-4">
          <Button type="button" variant="outline" onClick={() => handleClose(false)} className="flex-1 h-12" data-testid="drawer-cancel-btn">Annulla</Button>
          <Button onClick={handleAddAndClose}
            disabled={tipoComponente === "ingrediente" ? (!nuovoIngrediente.is_acqua && !nuovoIngrediente.ingrediente_id) || !nuovoIngrediente.quantita : !nuovoIngrediente.ricetta_id || !nuovoIngrediente.quantita}
            className="flex-1 h-12 font-bold" data-testid="drawer-add-btn">
            <Check className="h-4 w-4 mr-1.5" />Aggiungi
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
